<script setup>
/**
 * @description 通用 ActionSheet（移动端底部弹出菜单）
 * @param {boolean} visible - 是否显示
 * @param {string} title - 顶部标题（可选）
 * @param {string} description - 顶部描述（可选）
 * @param {string} titleIcon - 顶部图标（可选，建议 28x28）
 * @param {string} titleIconAlt - 顶部图标 alt
 * @param {boolean} showClose - 是否显示右上角关闭按钮
 * @param {boolean} maskClosable - 点击遮罩是否可关闭
 * @param {boolean} escToClose - 是否支持 ESC 关闭
 * @param {string|number} maxHeight - 面板最大高度（建议 '60vh'）
 * @param {ActionSheetAction[]} actions - 单组 actions（与 groups 二选一）
 * @param {ActionSheetGroup[]} groups - 多组 actions（与 actions 二选一）
 * @param {boolean} showCancel - 是否显示取消按钮
 * @param {string} cancelText - 取消按钮文案
 * @emits update:visible - 显隐变化
 * @emits select - 点击选项 { action, groupIndex, actionIndex }
 * @emits cancel - 点击取消按钮
 * @emits close - 关闭（遮罩/ESC 等）
 * @slot header - 自定义头部（覆盖 title/description）
 * @slot item - 自定义 item（作用域：{ action, groupIndex, actionIndex }）
 */

// 1. Imports
import { computed, onUnmounted, watch } from 'vue'
import { lockBodyScroll, unlockBodyScroll } from '~/utils/modal'

/**
 * @typedef {Object} ActionSheetAction
 * @property {string} label - 展示文案
 * @property {string} [value] - 业务值
 * @property {string} [description] - 辅助描述（可选）
 * @property {string} [icon] - 左侧图标 URL（可选）
 * @property {string} [iconAlt] - 左侧图标 alt（可选）
 * @property {boolean} [disabled] - 是否禁用
 */

/**
 * @typedef {Object} ActionSheetGroup
 * @property {string} [key] - 分组 key
 * @property {ActionSheetAction[]} actions - 分组 action 列表
 */

// 2. Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  titleIcon: {
    type: String,
    default: '',
  },
  titleIconAlt: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  maskClosable: {
    type: Boolean,
    default: true,
  },
  escToClose: {
    type: Boolean,
    default: true,
  },
  maxHeight: {
    type: [String, Number],
    default: '60vh',
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  actions: {
    type: Array,
    default: () => [],
  },
  groups: {
    type: Array,
    default: () => [],
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  cancelText: {
    type: String,
    default: '取消',
  },
})

// 3. Emits
const emit = defineEmits(['update:visible', 'select', 'cancel', 'close'])

const { withBase } = useBasePath()

// 4. Computed
const normalizedGroups = computed(() => {
  if (props.groups?.length) {
    return props.groups
      .filter((g) => Array.isArray(g?.actions) && g.actions.length)
      .map((g, idx) => ({
        key: g.key || `group_${idx}`,
        actions: g.actions,
      }))
  }
  if (props.actions?.length) {
    return [
      {
        key: 'group_0',
        actions: props.actions,
      },
    ]
  }
  return []
})

const hasHeader = computed(() => {
  return Boolean(props.title || props.description)
})

// 5. Methods
const close = () => {
  emit('update:visible', false)
  emit('close')
}

/**
 * @description 点击某个 action
 * @param {ActionSheetAction} action - action
 * @param {number} groupIndex - 分组索引
 * @param {number} actionIndex - action 索引
 * @returns {void}
 */
const handleActionClick = (action, groupIndex, actionIndex) => {
  if (action?.disabled) return
  emit('select', { action, groupIndex, actionIndex })
  emit('update:visible', false)
}

const handleCancel = () => {
  emit('cancel')
  emit('update:visible', false)
}

/**
 * @description 处理 ESC 关闭（仅在客户端生效）
 * @returns {void}
 */
const handleEscToClose = (e) => {
  if (!props.escToClose) return
  if (e.key !== 'Escape') return
  close()
}

watch(
  () => props.visible,
  (v) => {
    if (!import.meta.client) return
    if (v) {
      lockBodyScroll()
      window.addEventListener('keydown', handleEscToClose)
      return
    }
    unlockBodyScroll()
    window.removeEventListener('keydown', handleEscToClose)
  },
  { immediate: true }
)

onUnmounted(() => {
  if (!import.meta.client) return
  unlockBodyScroll()
  window.removeEventListener('keydown', handleEscToClose)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="base-action-sheet-fade">
      <div v-if="visible" class="base-action-sheet-root" aria-modal="true" role="dialog">
        <div class="base-action-sheet__mask" @click="maskClosable ? close() : undefined" />

        <Transition name="base-action-sheet-slide">
          <div
            v-if="visible"
            class="base-action-sheet"
            :style="{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }"
            @click.stop
          >
            <div v-if="$slots.header || hasHeader || showClose" class="base-action-sheet__header">
              <slot name="header">
                <div class="base-action-sheet__title">
                  <img
                    v-if="titleIcon"
                    class="base-action-sheet__title-icon"
                    :src="titleIcon"
                    :alt="titleIconAlt || 'icon'"
                  />
                  <span class="base-action-sheet__title-text">{{ title || '请选择' }}</span>
                </div>
                <div v-if="description" class="base-action-sheet__desc">{{ description }}</div>
              </slot>

              <button
                v-if="showClose"
                class="base-action-sheet__close"
                type="button"
                @click="close"
              >
                <img :src="withBase('/images/icons/close.svg')" alt="关闭" />
              </button>
            </div>

            <div class="base-action-sheet__list">
              <slot />
              <div
                v-for="(group, groupIndex) in normalizedGroups"
                :key="group.key"
                class="base-action-sheet__group"
              >
                <button
                  v-for="(action, actionIndex) in group.actions"
                  :key="`${group.key}_${action.label}_${actionIndex}`"
                  class="base-action-sheet__menu-item"
                  type="button"
                  :class="{ 'base-action-sheet__menu-item--disabled': action.disabled }"
                  :aria-disabled="action.disabled ? 'true' : 'false'"
                  @click="handleActionClick(action, groupIndex, actionIndex)"
                >
                  <slot
                    name="item"
                    :action="action"
                    :group-index="groupIndex"
                    :action-index="actionIndex"
                  >
                    <img
                      v-if="action.icon"
                      class="base-action-sheet__item-icon"
                      :src="action.icon"
                      :alt="action.iconAlt || action.label"
                    />
                    <div class="base-action-sheet__item-content">
                      <h4 class="base-action-sheet__item-title">{{ action.label }}</h4>
                      <p v-if="action.description" class="base-action-sheet__item-desc">
                        {{ action.description }}
                      </p>
                    </div>
                    <!-- <img
                      class="base-action-sheet__item-arrow"
                      src="/images/icons/arrow_right.svg"
                      alt="箭头"
                    /> -->
                  </slot>
                </button>
              </div>

              <div
                v-if="showCancel"
                class="base-action-sheet__group base-action-sheet__group--cancel"
              >
                <button
                  class="base-action-sheet__menu-item base-action-sheet__menu-item--cancel"
                  type="button"
                  @click="handleCancel"
                >
                  <span class="base-action-sheet__cancel-text">{{ cancelText }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.base-action-sheet-root {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.base-action-sheet__mask {
  position: absolute;
  inset: 0;
  background-color: rgba($color-black, 0.4);
  backdrop-filter: blur($spacing-2xs);
  z-index: 0;
}

.base-action-sheet {
  width: 100%;
  background-color: $bg-white;
  border-radius: 12px 12px 0 0;
  box-shadow: $shadow-dropdown-level1;
  overflow-y: auto;
  padding-bottom: calc(env(safe-area-inset-bottom));
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: $spacing-lg $spacing-lg $spacing-md;
    border-bottom: 1px solid $border-light-color;
    position: relative;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    font-size: $font-size-card-subtitle;
    font-weight: 600;
    color: $text-title-color;
    margin: 0;
  }

  &__title-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: $bg-color;
    padding: $spacing-2xs;
  }

  &__title-text {
    line-height: $line-height-lg;
  }

  &__desc {
    margin-top: $spacing-xs;
    font-size: $font-size-card-content;
    line-height: $line-height-lg;
    color: $text-subtitle-color;
    text-align: center;
  }

  &__close {
    width: 30px;
    height: 30px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    padding: 0;
    position: absolute;
    top: 50%;
    right: $spacing-sm;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background-color: $bg-light-gray;
      }
    }

    img {
      width: 16px;
      height: 16px;
      opacity: 0.6;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__group {
    display: flex;
    flex-direction: column;
  }

  &__menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border: none;
    background: transparent;
    padding: $spacing-md $spacing-lg;
    cursor: pointer;
    transition: background-color 0.2s ease;
    text-align: left;
    touch-action: manipulation;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background-color: $bg-light-gray;
      }
    }

    &:active {
      background-color: rgba($text-title-color, 0.04);
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.5;

      &:active {
        background-color: transparent;
      }
    }

    &--cancel {
      justify-content: center;
      color: $text-title-color;
      font-size: 16px;
      line-height: 24px;
    }
  }

  &__item-icon {
    width: 24px;
    height: 24px;
    margin-right: $spacing-sm;
    opacity: 0.7;
    flex: 0 0 auto;
  }

  &__item-content {
    display: flex;
    flex-direction: column;
    gap: $spacing-2xs;
    flex: 1;
    min-width: 0;
  }

  &__item-title {
    font-size: 16px;
    font-weight: 400;
    color: $text-title-color;
    margin: 0;
    line-height: $line-height-lg;
    text-align: center;
  }

  &__item-desc {
    font-size: $font-size-caption;
    color: $text-subtitle-color;
    margin: 0;
    line-height: 1.4;
    @include text-ellipsis(2);
  }

  &__item-arrow {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  &__group--cancel {
    border-top: 12px solid #f2f3f5;
  }

  &__cancel-text {
    font-size: $font-size-card-subtitle;
    color: $text-title-color;
  }
}

.base-action-sheet-fade-enter-active,
.base-action-sheet-fade-leave-active {
  transition: opacity 0.2s ease;
}

.base-action-sheet-fade-enter-from,
.base-action-sheet-fade-leave-to {
  opacity: 0;
}

.base-action-sheet-slide-enter-active,
.base-action-sheet-slide-leave-active {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.base-action-sheet-slide-enter-from,
.base-action-sheet-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
